/*
* This code is the intellectual property of Bulb Inc. and other software publishers;
* it may not be altered, copied or disclosed without prior written approval.
* Copyright 2022, Bulb Inc. For more information, please email contact@hellobulb.com.
*/
export enum MenuListType {
  MENU = 'MENU',
  RADIO = 'RADIO',
}

/**
 * Changes the color of the MenuItem label and its icon.
 */
export enum ColorOptions {
  'ALERT' = 'var(--bulb-darker-red-color)',
}

/**
 * Interface for MenuItemClickEvent.
 */
export interface MenuItemClickEvent {
  $event: MouseEvent;
  menuItem: MenuItem;
}

/**
 * Different render options for search MenuItem. Used to set different conditionals within the component templates.
 */
export enum TypeOptions {
  TOGGLE = 'TOGGLE',
  LINK = 'LINK',
  BUTTON = 'BUTTON',
  MENU_PANEL = 'MENU_PANEL',
}

/**
 * The MenuItem interface is used to define the structure of the menu items.
 * The properties here affect how the component renders the menu item.
 */
export type MenuItem = {

  // Unique id of the menu item.
  id?: string;

  // Show animated dot on menu item.
  showAnimatedDot?: boolean;

  // The label to display on the menu item.
  label: string;

  // Controls visiblity of the menu item label.
  showLabel?: boolean;

  // The name of the SVG icon to use.
  icon: string;

  /**
   * If the SVG icon doesn't work with the Svg-Icon component, a direct link to an SVG
   * asset can be passed to display an img tag instead
   */
  iconSrc?: string;

  // A function to call when clicked.
  command?: Function;

  // Close the menu completely when menu item clicked.
  closeMenuOnClick?: boolean;

  // Optional text to display alongside the icon. Should be brief.
  descriptiveText?: string;

  /**
   * Svg Icon that is placed to the right of the text in place of the descriptive text
   * This is an Svg-icon key
   */
  descriptiveIcon?: string;

  /**
   * URL that is opened in a new tab if the icon is clicked.
   */
  descriptiveIconURL?: string;

  /**
   * Aria-label for the icon.
   */
  descriptiveIconLabel?: string;

  /**
  * If the SVG icon doesn't work with the Svg-Icon component, a direct link to an SVG asset
  * can be passed to display in an img tag instead
  */
  descriptiveIconSrc?: string;

  /**
   * Optional descriptive icon style class
   */
  descriptiveIconStyle?: string;

  // Disabled click events on the menu item.
  disabled?: boolean;

  // Action to perform if the disabled menu item is clicked
  disabledAction?: Function;

  // Hide the menu item completely.
  hidden?: boolean;

  // The URL this item should link out to if it is of type LINK.
  link?: string;

  // The color of the menu item label and icon.
  color?: ColorOptions;

  // The type of menu item. Default is 'BUTTON', but can be set to 'TOGGLE' or 'LINK'.
  type?: TypeOptions;

  // Some data that is passed to the MenuItem.
  // The TOGGLE type uses this data to determine if the item is checked or not.
  model?: any;

  value?: any;

  // Unique name to identity form the list of menu items.
  name?: string;

  // Optional text to display as a tooltip when the user hovers over the menu item.
  tooltipLabel?: string;

  // data used to render a menu panel when clicking on this menu item.
  menuPanel?: {
    listType: MenuListType;
    menuItems: MenuItem[];
  };
};
